import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import IconButton from "../../ui/buttons/IconButton";
import { chipClassName } from "../../ui/styles/chipStyles";
import { classNames } from "../../ui/styles/classNames";
import type { ChatReference } from "./chatReference";

interface ReferenceChipsProps {
  references: readonly ChatReference[];
  onRemove: (reference: ChatReference) => void;
}

/**
 * The references a message will carry, above the composer's text (F96): each by the name its
 * owner gives it (a term's label, a document's key, an item's name), never its ID, with the
 * selected text shortened and in full on hover, and a way to drop it.
 */
export default function ReferenceChips({ references, onRemove }: ReferenceChipsProps) {
  const { t } = useTranslation();
  if (references.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label={t("chatReferences.chips")}>
      {references.map((reference) => (
        <li
          key={`${reference.reference} ${reference.quote ?? ""}`}
          className={classNames(chipClassName(), "app-reference-chip")}
          title={reference.quote ?? reference.name}
        >
          <span className="font-medium">{reference.name}</span>
          {reference.quote !== null ? <q className="app-text-muted min-w-0 truncate">{reference.quote}</q> : null}
          <IconButton
            label={t("chatReferences.remove", { name: reference.name })}
            className="h-5 w-5 shrink-0"
            onClick={() => { onRemove(reference); }}
          >
            <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
          </IconButton>
        </li>
      ))}
    </ul>
  );
}
